import React, { useState, useEffect, useCallback } from 'react';
import { reviewsApi, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Star, BadgeCheck } from 'lucide-react';

export const Stars = ({ rating, size = 'w-4 h-4' }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`${size} ${i <= Math.round(rating) ? 'fill-[#B8860B] text-[#B8860B]' : 'text-[#D6D3CE]'}`}
      />
    ))}
  </span>
);

const ReviewForm = ({ productId, onSubmitted }) => {
  const { t } = useLang();
  const { toast } = useToast();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await reviewsApi.create({ product_id: productId, rating, title, text });
      toast({
        title: t({ it: 'Recensione inviata!', en: 'Review submitted!' }),
        description: t({ it: 'Sarà pubblicata dopo l\'approvazione.', en: 'It will appear after approval.' }),
      });
      onSubmitted();
    } catch (error) {
      toast({ title: t({ it: 'Errore', en: 'Error' }), description: formatApiError(error), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F2EFE9] p-6 mb-8" data-testid="review-form">
      <h4 className="font-medium mb-4">{t({ it: 'Scrivi una recensione', en: 'Write a review' })}</h4>
      <div className="flex items-center gap-1 mb-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <button
            key={i}
            type="button"
            onClick={() => setRating(i)}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(0)}
            data-testid={`review-star-${i}`}
            aria-label={`${i} stelle`}
          >
            <Star
              className={`w-7 h-7 transition-colors ${
                i <= (hover || rating) ? 'fill-[#B8860B] text-[#B8860B]' : 'text-[#D6D3CE]'
              }`}
            />
          </button>
        ))}
      </div>
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={t({ it: 'Titolo della recensione', en: 'Review title' })}
        data-testid="review-title-input"
        className="w-full border border-[#E5E0D8] p-3 text-sm mb-3 focus:outline-none focus:border-[#1C1917]"
        maxLength={100}
      />
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t({ it: 'Racconta la tua esperienza con questo capo...', en: 'Tell us about your experience with this garment...' })}
        rows={4}
        data-testid="review-text-input"
        className="w-full border border-[#E5E0D8] p-3 text-sm mb-4 focus:outline-none focus:border-[#1C1917]"
        maxLength={2000}
      />
      <button
        onClick={submit}
        disabled={!rating || !title.trim() || !text.trim() || submitting}
        data-testid="review-submit-btn"
        className="px-6 py-3 bg-[#1C1917] text-white text-sm hover:bg-[#57534E] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {submitting
          ? t({ it: 'Invio...', en: 'Submitting...' })
          : t({ it: 'Invia recensione', en: 'Submit review' })}
      </button>
    </div>
  );
};

export default function ProductReviews({ productId }) {
  const { t } = useLang();
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReviews = useCallback(async () => {
    try {
      const response = await reviewsApi.getForProduct(productId);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  if (loading) {
    return (
      <div className="mt-16 pt-12 border-t border-[#E5E0D8]" data-testid="reviews-section">
        <div className="animate-pulse space-y-4">
          <div className="h-7 bg-[#E5E0D8] w-40" />
          <div className="h-4 bg-[#E5E0D8] w-64" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { reviews, rating_avg, rating_count, can_review, my_review } = data;

  return (
    <div className="mt-16 pt-12 border-t border-[#E5E0D8]" data-testid="reviews-section">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h2 className="text-2xl font-light tracking-tight mb-2">
            {t({ it: 'Recensioni', en: 'Reviews' })}
          </h2>
          {rating_count > 0 ? (
            <div className="flex items-center gap-3" data-testid="reviews-summary">
              <Stars rating={rating_avg} size="w-5 h-5" />
              <span className="text-lg font-medium">{rating_avg}</span>
              <span className="text-sm text-[#57534E]">
                ({rating_count} {t(rating_count === 1 ? { it: 'recensione', en: 'review' } : { it: 'recensioni', en: 'reviews' })})
              </span>
            </div>
          ) : (
            <p className="text-sm text-[#57534E]">
              {t({ it: 'Nessuna recensione ancora', en: 'No reviews yet' })}
            </p>
          )}
        </div>
      </div>

      {isAuthenticated && can_review && (
        <ReviewForm productId={productId} onSubmitted={fetchReviews} />
      )}

      {isAuthenticated && my_review && my_review.status === 'pending' && (
        <div className="bg-[#F2EFE9] p-4 mb-8 text-sm text-[#57534E]" data-testid="review-pending-notice">
          {t({
            it: 'La tua recensione è in attesa di approvazione. Grazie!',
            en: 'Your review is awaiting approval. Thank you!',
          })}
        </div>
      )}

      {isAuthenticated && my_review && my_review.status === 'rejected' && (
        <div className="bg-[#F2EFE9] p-4 mb-8 text-sm text-[#C05746]" data-testid="review-rejected-notice">
          {t({
            it: 'La tua recensione non è stata approvata dalla moderazione.',
            en: 'Your review was not approved by moderation.',
          })}
        </div>
      )}

      {reviews.length > 0 && (
        <div className="space-y-6">
          {reviews.map((review, idx) => (
            <div key={review.review_id} className="pb-6 border-b border-[#E5E0D8]" data-testid={`review-${idx}`}>
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <Stars rating={review.rating} />
                <span className="font-medium text-sm">{review.customer_name}</span>
                {review.verified && (
                  <span className="inline-flex items-center gap-1 text-xs text-[#4A5D23]" data-testid={`review-verified-${idx}`}>
                    <BadgeCheck className="w-4 h-4" />
                    {t({ it: 'Acquisto verificato', en: 'Verified purchase' })}
                  </span>
                )}
                <span className="text-xs text-[#78716C] ml-auto">
                  {new Date(review.created_at).toLocaleDateString('it-IT')}
                </span>
              </div>
              <p className="font-medium mb-1">{review.title}</p>
              <p className="text-sm text-[#57534E] leading-relaxed">{review.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
